import { Box, Tooltip } from '@mui/material';

import { environmentLabel, FIREBASE_PROJECT_ID } from '../services/environment';
import { color } from '../theme/foundation';

/**
 * A permanent ribbon on every non-production build naming the Firebase project
 * behind the page.
 *
 * The problem it solves is mundane and expensive: the test and live apps are
 * pixel-identical, and once `rm24.ee` and `rendimees24-dev.web.app` are both
 * open in tabs, there is nothing to tell you which one you are typing a real
 * client's data into. Deployments, screenshots and bug reports have the same
 * ambiguity.
 *
 * Renders `null` on production, so the live site is untouched. The check is on
 * the project id the Firebase SDK was actually initialised with — not a build
 * flag — so a prod deploy accidentally built against the dev config still shows
 * the badge, which is exactly the case worth catching.
 *
 * Labels are hardcoded English like the DEV role-switcher and intentionally
 * kept out of the i18n catalogs: this is developer scaffolding, not product UI.
 *
 * UX 1.0d: it was the only colour-contrast and `region` failure on every dev
 * view — white 11 px text on `warning.dark` at 85 % opacity, outside any
 * landmark. Now an opaque foundation token (white on #9A3F0B, 6.80:1; on the
 * error red, 6.47:1 — both in foundation.ts contrastPairs) inside its own
 * short-named <aside>.
 */
export function EnvBadge() {
  const label = environmentLabel();
  if (!label) return null;

  const isMisconfigured = label === 'NO CONFIG';

  return (
    <Tooltip
      title={
        isMisconfigured
          ? 'VITE_FIREBASE_PROJECT_ID is empty — this build has no Firebase config.'
          : `Connected to Firebase project "${FIREBASE_PROJECT_ID}". Not the live site.`
      }
      placement="right"
    >
      <Box
        component="aside"
        aria-label={`Environment: ${label}`}
        sx={{
          position: 'fixed',
          // Bottom-left: the language switcher owns the top-right on every
          // screen (CLAUDE.md § i18n) and the DEV role-switcher floats bottom-right.
          bottom: 'calc(8px + var(--worker-navigation-offset, 0px))',
          left: 8,
          zIndex: (t) => t.zIndex.tooltip + 1,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          fontFamily: 'monospace',
          color: color.onBrand,
          bgcolor: isMisconfigured ? color.error : color.brandText,
          pointerEvents: 'auto',
          userSelect: 'none',
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}
